import { DataGrid, DataGridProps } from "@itk/mui-components";
import { hasValue } from "@itk/typescript-utils";
import { GridCallbackDetails, GridColumnOrderChangeParams, GridPaginationModel, GridPinnedColumnFields, GridSortModel, GridValidRowModel } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import { GenericListColumnAlignment, GenericListSortingDirection, TDataGridColumn, TDataGridSettings } from "./datagrid-with-identity-types";
import { DATA_GRID_ACTION_COLUMN_NAME } from "./datagrid-with-identity-types";

type Props<T extends GridValidRowModel> = DataGridProps<T> & { dataGridSettings?: TDataGridSettings; handleSaveSettings: (value: Partial<TDataGridSettings>) => void };

export const DataGridWithIdentity = <T extends GridValidRowModel>(props: Props<T>) => {
  const { dataGridSettings, handleSaveSettings, ...restProps } = props;

  const userDataColumns: TDataGridColumn[] = useMemo(() => {
    return restProps.columns.map((column, index): TDataGridColumn => {
      const userSettingColumn = (dataGridSettings?.columns && dataGridSettings?.columns.find(userDataColumn => userDataColumn?.field === column.field)) || {};
      return {
        field: column.field,
        alignment: column.align === "left" ? GenericListColumnAlignment.Left : column.align === "right" ? GenericListColumnAlignment.Right : GenericListColumnAlignment.Center,
        columnVisibility: true,
        columnWidth: column.width || 10,
        showMore: true,
        sortOrder: index,
        ...userSettingColumn
      };
    });
  }, [restProps.columns, dataGridSettings?.columns]);

  const combinedColumns = useMemo(() => {
    return restProps.columns
      .map((column, index) => {
        const userSettingColumn = dataGridSettings?.columns && dataGridSettings?.columns.find(userDataColumn => userDataColumn?.field === column.field);
        return {
          ...column,
          columnSortOrder: userSettingColumn?.sortOrder || userSettingColumn?.sortOrder === 0 ? userSettingColumn?.sortOrder : index
        };
      })
      .sort((a, b) => {
        if (a.columnSortOrder > b.columnSortOrder) {
          return 1;
        } else if (b.columnSortOrder > a.columnSortOrder) {
          return -1;
        }
        return 0;
      });
  }, [restProps.columns, dataGridSettings?.columns]);

  const pinnedColumns = useMemo(() => {
    if (restProps.pinnedColumns) return restProps.pinnedColumns;
    const returnValue: { left: string[]; right: string[] } = { left: [], right: [] };
    if (dataGridSettings && dataGridSettings?.pinnedColumns?.left?.length > 0) {
      returnValue.left = [...(dataGridSettings?.pinnedColumns?.left || [])];
    }
    if (dataGridSettings && dataGridSettings?.pinnedColumns?.right?.length > 0) {
      returnValue.right = [...(dataGridSettings?.pinnedColumns?.right || []), DATA_GRID_ACTION_COLUMN_NAME];
    }
    return returnValue;
  }, [dataGridSettings?.pinnedColumns, restProps.pinnedColumns]);

  const sortModel = useMemo((): GridSortModel | undefined => {
    if (restProps.sortModel) return restProps.sortModel;
    if (dataGridSettings && hasValue(dataGridSettings?.sortColumnId) && hasValue(dataGridSettings?.sortDirection)) {
      return [
        {
          field: dataGridSettings?.sortColumnId,
          sort: dataGridSettings?.sortDirection === GenericListSortingDirection.Ascending ? "asc" : "desc"
        }
      ];
    }
  }, [dataGridSettings?.sortColumnId, dataGridSettings?.sortDirection, restProps.sortModel]);

  const onPaginationModelChange = (model: GridPaginationModel, details: GridCallbackDetails) => {
    if (restProps.onPaginationModelChange) restProps.onPaginationModelChange(model, details);

    handleSaveSettings({ ...dataGridSettings, pageSize: model.pageSize });
  };

  const onPinnedColumnsChange = (params: GridPinnedColumnFields, details: GridCallbackDetails) => {
    if (restProps.onPinnedColumnsChange) restProps.onPinnedColumnsChange(params, details);

    handleSaveSettings({ ...dataGridSettings, pinnedColumns: { left: params.left ?? [], right: params.right?.filter(r => r !== DATA_GRID_ACTION_COLUMN_NAME) ?? [] } });
  };

  const onSortModelChange = (model: GridSortModel, details: GridCallbackDetails) => {
    if (restProps.onSortModelChange) restProps.onSortModelChange(model, details);

    const direction = model?.[0]?.sort;
    handleSaveSettings({
      ...dataGridSettings,
      sortColumnId: model?.[0]?.field,
      sortDirection: direction && direction === "asc" ? GenericListSortingDirection.Ascending : GenericListSortingDirection.Descending
    });
  };

  const onColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const oldIndex = userDataColumns.find(co => co.field === params.column.field)?.sortOrder ?? params.oldIndex;

    let newOrderedColumns: TDataGridColumn[] = [];

    if (params.targetIndex < params.oldIndex) {
      newOrderedColumns = userDataColumns.map(co => ({
        ...co,
        sortOrder: co.field === params.column.field ? params.targetIndex : co.sortOrder >= params.targetIndex && co.sortOrder < oldIndex ? co.sortOrder + 1 : co.sortOrder
      }));
    } else {
      newOrderedColumns = userDataColumns.map(co => ({
        ...co,
        sortOrder: co.field === params.column.field ? params.targetIndex : co.sortOrder > oldIndex && co.sortOrder <= params.targetIndex ? co.sortOrder - 1 : co.sortOrder
      }));
    }
    handleSaveSettings({ ...dataGridSettings, columns: newOrderedColumns });
  };

  return (
    <DataGrid
      {...restProps}
      columns={combinedColumns}
      pinnedColumns={pinnedColumns}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      onPinnedColumnsChange={onPinnedColumnsChange}
      onPaginationModelChange={onPaginationModelChange}
      onColumnOrderChange={onColumnOrderChange}
    />
  );
};
